import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

import { env } from '@/env';
import { qgammonAuthHeaders, qgammonSocketAuth } from '@/lib/qgammon/auth';
import { QGAMMON_SOCKET_OPTIONS } from '@/lib/qgammon/socketOptions';
import { ServerEvents, type InviteAcceptedPayload } from '@qlashgeminiz/qgammon-types';
import { statusOfMatchState, type MatchStatus } from '@/lib/qgammon/matchStatus';
import type { Player } from '@qlashgeminiz/qgammon-rules';
import {
  leggiEsitoAllenamento,
  motivoNonAmmessoDel409,
  prendiPropostaAllenamento,
  type EsitoAllenamento,
} from '@/lib/qgammon/allenamentoInvito';

export type InviteStatus = 'idle' | 'creating' | 'pending' | 'accepted';

/** What `createInvite` resolves to / the pending invite the sheet renders. */
export interface PendingInvite {
  code: string;
  /** ISO timestamp at which the invite expires (for the countdown). */
  expiresAt: string;
  matchLength: 1 | 3 | 5;
  /**
   * Allenamento fra amici (cantiere 34): l'ECO del server dice che l'invito lo propone (`allenamento: "proposto"`). Solo nel ramo del letterale
   * del flag del coach; altrove assente.
   */
  allenamento?: boolean;
}

/**
 * The creator-side resolution: the freshly created match + the CREATOR's colour
 * (the `invite:accepted` push carries the creator's side) + the acceptor info.
 */
export interface InviteAccepted {
  matchId: string;
  /** The CREATOR's colour — used as `state.perspective` on navigation. */
  color: Player;
  opponent: InviteAcceptedPayload['opponent'];
  /**
   * ADR-0114 — quello che nasce e' un APPUNTAMENTO, non una partita, e il
   * server lo dice anche QUI, sul canale del creatore
   * (`qgammon-gameserver/src/invites/routes.ts:232` @ origin/main).
   *
   * 🔴 E' la meta' del difetto che si vede meno: chi ha creato l'invito riceve
   * questo evento **senza aver toccato niente**, e la app lo portava su una
   * schermata che diceva «In gioco». La correzione del solo lato accettante
   * avrebbe lasciato in piedi meta' bugia, proprio per l'utente che l'azione
   * l'aveva fatta.
   * ⚠️ `MatchStatus | undefined` e non un default a `'active'`: qui `undefined`
   * significa «un gameserver piu' vecchio dell'ADR non me l'ha detto», e il
   * default vive in un posto solo (`isWaitingMatch`).
   */
  status?: MatchStatus;
  /** Allenamento fra amici (cantiere 34): l'esito di quello proposto, se il server lo manda. Solo nel ramo del letterale. */
  allenamento?: EsitoAllenamento | null;
}

export interface InviteController {
  status: InviteStatus;
  /** The live invite once created (code + expiry), else null. */
  pending: PendingInvite | null;
  /** Set when the creator's socket receives `invite:accepted`. */
  accepted: InviteAccepted | null;
  /**
   * `rated` = il server ha rimandato `rated: true`: l'invito sarebbe una partita CLASSIFICATA, e per
   * L-2608 le partite fra amici sono fuori classifica. L'invito appena creato si ANNULLA (non resta
   * un codice classificato in giro) e non si mostra. `InviteSheet` lo dice con la frase generica di
   * «impossibile creare l'invito», che e' vera.
   */
  error: 'limit_reached' | 'create_failed' | 'rated' | `allenamento:${string}` | null;
  /** POST /invites → stash {code, expiresAt} and open the listening socket. */
  createInvite: (matchLength: 1 | 3 | 5) => void;
  /** DELETE /invites/:code (creator only) and reset to idle. */
  cancelInvite: (code: string) => void;
}

/**
 * Drives the CREATOR side of a challenge-by-consent invite from the TMA.
 *
 * Lifecycle (mirrors `useRankedQueue`): the listening Socket.IO connection is
 * opened ONLY while an invite is pending. `createInvite` POSTs `/invites`, and
 * on success opens a handshake-auth-only socket (no `match:join`) — the
 * `invite:accepted` event is fanned out by the gameserver to every one of the
 * creator's sockets (UserSocketIndex), so a listen-only socket receives it.
 * When it arrives we stash `accepted` and let the CALLER navigate to the match
 * with the creator's colour. `cancelInvite` DELETEs the code and tears the
 * socket down. The socket also closes on accept and on unmount.
 *
 * The invite endpoints live on the gameserver (`env.qgammonWsUrl`, :4001) and
 * use the same handshake/header auth as the ranked queue (centralised in
 * `@/lib/qgammon/auth`): a per-user Logto Bearer when signed in, else the
 * dev-bypass fallback. Per-user identity is what makes the invite creator and
 * acceptor distinct users instead of both being the shared "Dev" id.
 */
export function useInvite(): InviteController {
  const socketRef = useRef<Socket | null>(null);
  const [status, setStatus] = useState<InviteStatus>('idle');
  const [pending, setPending] = useState<PendingInvite | null>(null);
  const [accepted, setAccepted] = useState<InviteAccepted | null>(null);
  const [error, setError] = useState<InviteController['error']>(null);

  // Set on unmount so an in-flight `qgammonSocketAuth()` resolving after the
  // component is gone doesn't create (and leak) a socket the cleanup can't reach.
  const unmountedRef = useRef(false);

  // Tear the socket down once on unmount / when no invite is live.
  const closeSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      closeSocket();
    };
  }, [closeSocket]);

  const openSocket = useCallback(() => {
    if (socketRef.current) return;
    // Handshake auth resolves asynchronously (may mint/refresh a Logto token).
    void qgammonSocketAuth().then((auth) => {
      // A concurrent openSocket / a cancel that already opened-or-closed the
      // socket may have won the race, or we unmounted mid-flight; bail in either
      // case so we never leak a socket past unmount.
      if (socketRef.current || unmountedRef.current) return;
      const socket = io(env.qgammonWsUrl, { ...QGAMMON_SOCKET_OPTIONS, auth });
      socketRef.current = socket;

      socket.on(ServerEvents.InviteAccepted, (p: InviteAcceptedPayload) => {
        setAccepted(
          // Allenamento fra amici (cantiere 34): l'esito dell'allenamento proposto, letto come dato sconosciuto (V10). Ramo del letterale.
          import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true'
            ? {
                matchId: p.matchId,
                color: p.color,
                opponent: p.opponent,
                status: statusOfMatchState(p) ?? undefined,
                allenamento: leggiEsitoAllenamento((p as { allenamento?: unknown }).allenamento),
              }
            : {
                matchId: p.matchId,
                color: p.color,
                opponent: p.opponent,
                // Il payload lo porta a runtime; `InviteAcceptedPayload@0.2.0` non lo
                // dichiara ancora — stesso debito, stessa ragione e stessa forma di
                // `statusOfMatchState`, che e' il posto in cui vive la spiegazione.
                status: statusOfMatchState(p) ?? undefined,
              },
        );
        setStatus('accepted');
        // The invite is consumed — stop listening; the caller navigates next.
        closeSocket();
      });
    });
  }, [closeSocket]);

  const createInvite = useCallback(
    (matchLength: 1 | 3 | 5) => {
      setError(null);
      setAccepted(null);
      setStatus('creating');
      void (async () => {
        try {
          // Logto Bearer if signed in, else dev-bypass headers (dev only).
          const headers = await qgammonAuthHeaders(true);
          const res = await fetch(`${env.qgammonWsUrl}/invites`, {
            method: 'POST',
            headers,
            // 🔴 `rated: false` — Luca, L-2608 (a), 30/09: «tutte le partite fra amici fuori
            // classifica, codice compreso». Ridondante con la regola del server (pezzo Q di
            // qgammon-orcr: su `/invites` assente = fuori classifica, per tutti i client), ma
            // innocuo: lo schema di oggi non e' stretto e scarta il campo che non conosce
            // [C QGammon `origin/main@8907279`, `invites/routes.ts:53`]. Letterale booleano.
            // Allenamento fra amici (cantiere 34): la proposta di chi invita la mette la casella (`prendiPropostaAllenamento`, consumata qui),
            // SOLO nel ramo del letterale del flag del coach. In produzione il ramo sparisce e il corpo e' quello di sempre (V11, P4 = 0).
            body: JSON.stringify(
              import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' && prendiPropostaAllenamento()
                ? { matchLength, rated: false, allenamento: true }
                : { matchLength, rated: false },
            ),
          });
          if (res.status === 409) {
            // Allenamento fra amici: il 409 `allenamento_non_ammesso` ha un `motivo` e si dice in chiaro, non come «troppi inviti». Ramo del letterale.
            if (import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true') {
              const motivo = motivoNonAmmessoDel409(await res.clone().json().catch(() => null));
              if (motivo !== null) {
                setError(`allenamento:${motivo}`);
                setStatus('idle');
                return;
              }
            }
            setError('limit_reached');
            setStatus('idle');
            return;
          }
          if (!res.ok) throw new Error(`create failed: ${res.status}`);
          const body = (await res.json()) as {
            code: string;
            matchLength: 1 | 3 | 5;
            expiresAt: string;
            rated?: unknown;
            allenamento?: unknown;
          };
          // 🔴 SI RILEGGE L'ECO, come per la coda (`lib/qgammon/queueMode.ts`): se il server dice
          // `rated: true`, questo codice farebbe una partita che scrive ELO, e chi l'ha mandato ha
          // scelto un invito fuori classifica. Non si mostra, e si annulla sul server — un codice
          // classificato lasciato vivo si puo' ancora accettare da un altro telefono.
          // ⚠️ `rated` ASSENTE invece si prosegue: e' il server di oggi, che l'eco non lo manda
          // ancora, e per la regola del server (Q) sugli inviti l'assenza vale «fuori classifica».
          // Fermarsi qui romperebbe ogni invito finche' Q non e' in linea (contratto `ranked-bg`, T3).
          if (body.rated === true) {
            setError('rated');
            setStatus('idle');
            void deleteInvite(body.code);
            return;
          }
          setPending(
            import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true'
              ? { code: body.code, expiresAt: body.expiresAt, matchLength: body.matchLength, allenamento: body.allenamento === 'proposto' }
              : { code: body.code, expiresAt: body.expiresAt, matchLength: body.matchLength },
          );
          setStatus('pending');
          openSocket();
        } catch {
          setError('create_failed');
          setStatus('idle');
        }
      })();
    },
    [openSocket],
  );

  const cancelInvite = useCallback(
    (code: string) => {
      setStatus('idle');
      setPending(null);
      setAccepted(null);
      setError(null);
      closeSocket();
      void deleteInvite(code);
    },
    [closeSocket],
  );

  return { status, pending, accepted, error, createInvite, cancelInvite };
}

/** Fire-and-forget DELETE of an invite (creator only): `cancelInvite`, and a refused echo. */
async function deleteInvite(code: string): Promise<void> {
  try {
    const headers = await qgammonAuthHeaders();
    await fetch(`${env.qgammonWsUrl}/invites/${encodeURIComponent(code)}`, {
      method: 'DELETE',
      headers,
    });
  } catch {
    /* best-effort */
  }
}
